import React, { memo, useEffect, useCallback, useMemo } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { message, Row, Tag, Col } from "antd";
import { PRIVATE_ROUTES } from "app/routers/Router.config";
import { ITabWithModalType } from "app/types/tabType";
import Permission, {
  hasPermission,
} from "app/components/Permission/Permission";
import { PermissionEnum } from "app/constants/permissionConst";
import Table, { RowEditButtons } from "app/components/Table/Table";
import Button from "app/components/Button/Button";
import TestItemsModal from "./components/TestItemsModal/TestItemsModal";
import styles from "./TestItemsTabPane.module.scss";
import { useFetchPaginatedData, useDeleteData } from "app/hooks/hooks";
import { TableProps } from "antd/lib/table";
import qs from "query-string";
import { getOrderBy } from "app/helpers/apiHelper";
import {
  ListWrapperTestTypeDTO,
  TestTypesApiV2TestTypesGetRequest,
  TestTypeDTO,
  TestTypeBrand,
} from "@generated/v2";
import { testTypesGet, testTypesDelete } from "app/api/v2/testTypesApi";
import { randomMaxMin } from "app/helpers/utilHelper";
import ErrorFallbackUI from "app/components/ErrorFallbackUI";
import { MeasurementMethod } from "@generated/v2";
import _camelCase from "lodash/camelCase";
import cx from "classnames";
import TabPane from "app/components/Tabs/TabPane";
import { enumValueLookup } from "app/helpers/enumHelper";
import Select, { Option } from "app/components/Select/Select";
import { useInstanceValue } from "app/hooks/useInstanceValue";

const TABLE_SCROLL = { x: 1200 };

const TestItemsTabPane = ({
  activeTab,
  redirect,
  modalConfig,
  ...rest
}: ITabWithModalType) => {
  const { t } = useTranslation();
  const location = useLocation();
  const history = useHistory();

  const historyRef = useInstanceValue(history);
  const redirectRef = useInstanceValue(redirect);
  const locationRef = useInstanceValue(location);

  /** Delete data hook */
  const {
    loading: deleteLoading,
    isSuccess: deleteSuccess,
    isError: deleteError,
    id: deleteId,
    deleteData,
  } = useDeleteData<void>(testTypesDelete);

  const measurementMethod = useMemo<MeasurementMethod>(() => {
    const query = qs.parse(location.search).measurementMethod;

    return enumValueLookup(
      MeasurementMethod,
      Array.isArray(query) ? query[0] : query
    );
  }, [location.search]);

  const brand = useMemo<TestTypeBrand>(() => {
    const query = qs.parse(location.search).brand;

    return enumValueLookup(
      TestTypeBrand,
      Array.isArray(query) ? query[0] : query
    );
  }, [location.search]);

  /** Get Test Types data */
  const {
    data: testTypes,
    pagination,
    orderBy,
    loading,
    refetch,
  } = useFetchPaginatedData<
    ListWrapperTestTypeDTO,
    TestTypesApiV2TestTypesGetRequest
  >(testTypesGet, {
    withOrderBy: activeTab,
    params: {
      measurementMethod,
      brand,
    },
  });

  /** Listens for test item deletion success, or error */
  useEffect(() => {
    if (deleteSuccess) {
      message.success(t("testItems.deleteSuccess"));
      refetch();
    } else if (deleteError) {
      message.error(t("testItems.deleteError"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteSuccess, deleteError]);

  const handleTableChange = useCallback<TableProps<TestTypeDTO>["onChange"]>(
    (pagination, filters, sorter) => {
      // Keep the old search params
      const currentSearch = qs.parse(locationRef.current.search);

      // and overwrite or add new values
      const values = {
        ...currentSearch,
        page: pagination.current,
        orderBy:
          (sorter.order && getOrderBy(sorter.columnKey, sorter.order)) ||
          undefined,
      };

      historyRef.current.push({
        pathname: locationRef.current.pathname,
        search: qs.stringify(values, {
          arrayFormat: "comma",
        }),
      });
    },
    [historyRef, locationRef]
  );

  const handleMeasurementMethod = useCallback(
    (value: MeasurementMethod) => {
      // Keep the old search params
      const currentSearch = qs.parse(locationRef.current.search);

      // change page to 1 if measurementMethod is changed
      if (currentSearch?.measurementMethod !== value) {
        currentSearch.page = "1";
      }

      // and overwrite or add new values
      const values = {
        ...currentSearch,
        measurementMethod: value,
      };

      historyRef.current.push({
        pathname: locationRef.current.pathname,
        search: qs.stringify(values, {
          arrayFormat: "comma",
        }),
      });
    },
    [historyRef, locationRef]
  );

  const handleTestTypeBrand = useCallback(
    (value: TestTypeBrand) => {
      // Keep the old search params
      const currentSearch = qs.parse(locationRef.current.search);

      // change page to 1 if measurementMethod is changed
      if (currentSearch?.brand !== value) {
        currentSearch.page = "1";
      }

      // and overwrite or add new values
      const values = {
        ...currentSearch,
        brand: value,
      };

      historyRef.current.push({
        pathname: locationRef.current.pathname,
        search: qs.stringify(values, {
          arrayFormat: "comma",
        }),
      });
    },
    [historyRef, locationRef]
  );

  const actionsColumn = useMemo<TableProps<TestTypeDTO>["columns"]>(() => {
    if (
      !hasPermission([
        PermissionEnum.TEST_TYPES_TEST_ITEMS_WRITE,
        PermissionEnum.TEST_TYPES_TEST_ITEMS_DELETE,
      ])
    ) {
      return [];
    }

    return [
      {
        key: "actions",
        fixed: "right",
        width: 130,
        render: (text: any, record: any) => (
          <RowEditButtons
            id={record.id}
            onEdit={() =>
              redirectRef.current(
                PRIVATE_ROUTES.TEST_TYPES_EDIT_SCREEN.path,
                record.id
              )
            }
            onDelete={deleteData}
            deleteTitle={t("testItems.columnDeleteConfirm")}
            deleteConfirm={t("testItems.columnDeleteOk")}
            deleteCancel={t("testItems.columnDeleteCancel")}
            deleteLoading={deleteId === record.id && deleteLoading}
          />
        ),
      },
    ];
  }, [deleteData, deleteId, deleteLoading, redirectRef, t]);

  const columns = useMemo<TableProps<TestTypeDTO>["columns"]>(
    () => [
      {
        title: t("testItems.columnName"),
        key: "name",
        dataIndex: "name",
        sorter: true,
        sortOrder: (orderBy.key === "name" && orderBy.direction) || undefined,
      },
      {
        title: t("pageFilter.measurementMethod"),
        key: "measurementMethod",
        dataIndex: "measurementMethod",
        width: 200,
        render: (text: string, record: TestTypeDTO) => (
          <Tag
            className={cx(
              styles.tag,
              MeasurementMethod.Quantitative === text
                ? styles.quantitativeTag
                : styles.qualitativeTag
            )}
          >
            {t(`measurementMethod.${_camelCase(text)}`)}
          </Tag>
        ),
      },
      {
        title: t("pageFilter.testTypeBrand"),
        key: "testTypebrand",
        dataIndex: "brand",
        width: 100,
      },
      {
        title: t("testItems.columnCount"),
        key: "substances",
        dataIndex: "substances.length",
        width: 100,
        render: (_, { substances, measurementMethod }: TestTypeDTO) => (
          <>
            {measurementMethod === MeasurementMethod.Qualitative
              ? substances.length
              : "---"}
          </>
        ),
      },
      {
        title: t("testItems.columnPositive"),
        key: "positiveValue",
        dataIndex: "positiveValue",
        width: 120,
        sorter: true,
        sortOrder:
          (orderBy.key === "positiveValue" && orderBy.direction) || undefined,
        render: (_, { positiveValue, measurementMethod }: TestTypeDTO) => (
          <>
            {measurementMethod === MeasurementMethod.Qualitative
              ? positiveValue
              : "---"}
          </>
        ),
      },
      {
        title: t("testItems.columnNegative"),
        key: "negativeValue",
        dataIndex: "negativeValue",
        width: 130,
        sorter: true,
        sortOrder:
          (orderBy.key === "negativeValue" && orderBy.direction) || undefined,
        render: (_, { negativeValue, measurementMethod }: TestTypeDTO) => (
          <>
            {measurementMethod === MeasurementMethod.Qualitative
              ? negativeValue
              : "---"}
          </>
        ),
      },
      {
        title: t("testItems.measurableRange"),
        key: "quantitativeRange",
        dataIndex: "quantitativeRange",
        width: 150,
        render: (_, { quantitativeRange }: TestTypeDTO) => (
          <>
            {quantitativeRange ? (
              <>
                {quantitativeRange.measurableRangeMin} -{" "}
                {quantitativeRange.measurableRangeMax}
              </>
            ) : (
              "---"
            )}
          </>
        ),
      },
      {
        title: t("testItems.negativeRange"),
        key: "quantitativeRange",
        dataIndex: "quantitativeRange",
        width: 150,
        render: (_, { quantitativeRange }: TestTypeDTO) => (
          <>
            {quantitativeRange ? (
              <>
                {quantitativeRange.negativeRangeMin} -{" "}
                {quantitativeRange.negativeRangeMax}
              </>
            ) : (
              "---"
            )}
          </>
        ),
      },
      ...actionsColumn,
    ],
    [actionsColumn, orderBy.direction, orderBy.key, t]
  );

  return (
    <ErrorFallbackUI>
      <TabPane {...rest}>
        <Permission
          requiredPermissions={[PermissionEnum.TEST_TYPES_TEST_ITEMS_WRITE]}
        >
          <Row align="middle" type="flex" justify="space-between">
            <Row align="middle" type="flex" gutter={12}>
              <Col>
                <Select
                  placeholder={t("pageFilter.measurementMethod")}
                  size="large"
                  onChange={handleMeasurementMethod}
                  value={measurementMethod}
                  className={styles.measurementMethodFilter}
                  allowClear
                  optionFilterProp="children"
                >
                  {Object.keys(MeasurementMethod).map(key => (
                    <Option
                      key={MeasurementMethod[key]}
                      value={MeasurementMethod[key].toString()}
                    >
                      {t(`measurementMethod.${_camelCase(key)}`)}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col>
                <Select
                  placeholder={t("pageFilter.testTypeBrand")}
                  size="large"
                  onChange={handleTestTypeBrand}
                  value={brand}
                  className={styles.measurementMethodFilter}
                  allowClear
                  optionFilterProp="children"
                >
                  {Object.keys(TestTypeBrand).map(key => (
                    <Option
                      key={TestTypeBrand[key]}
                      value={TestTypeBrand[key].toString()}
                    >
                      {key}
                    </Option>
                  ))}
                </Select>
              </Col>
            </Row>
            <Button
              className={styles.button}
              type="primary"
              size="large"
              onClick={() =>
                redirect(PRIVATE_ROUTES.TEST_TYPES_CREATE_SCREEN.path)
              }
              icon="plus"
            >
              {t("testItems.buttonCreate")}
            </Button>
          </Row>
          {!loading && (
            <TestItemsModal
              show={modalConfig && modalConfig.show ? modalConfig.show : false}
              data={
                modalConfig &&
                testTypes.find(item => item.id?.toString() === modalConfig.edit)
              }
              close={() => redirect(PRIVATE_ROUTES.TEST_TYPES_SCREEN.path)}
              submit={refetch}
              key={`testItemEdited-${randomMaxMin()}`}
            />
          )}
        </Permission>
        <Table
          className={styles.table}
          dataSource={testTypes}
          pagination={pagination}
          loading={loading}
          onChange={handleTableChange}
          scroll={TABLE_SCROLL}
          columns={columns}
        />
      </TabPane>
    </ErrorFallbackUI>
  );
};

export default memo(TestItemsTabPane);
